/*
 * Copyright © 2026 Medaica, Inc
 *
 * All rights reserved.
 *
 * This code is confidential and proprietary information belonging to Medaica, Inc.
 * Unauthorized copying, distribution, or use of this code, in whole or in part,
 * is strictly prohibited, and may constitute a violation of intellectual property rights.
 *
 * If you have received this code in error, please notify the owner immediately
 * at support@medaica.com and delete this file from your system.
 */

// pcg-report core: input-contract adapter (NEW CODE — no prototype counterpart).
//
// The production platform exports `medaica.pcg.exam_review_export` (schema
// medaica.pcg.review-export-schema.v1): a wrapper holding one entry per reviewed
// recording — each with a viewer disposition (accepted/rejected/none) and a full
// per-recording `evidencePackage` in exactly the MedaicaPcgViewerCardiacReportHandoff
// shape the report prototype consumes — plus an exam-level timing-consistency block.
//
// The report prototype consumes ONE handoff package (from /handoff/pcg/latest).
// This adapter bridges the two. Every mismatch it has to paper over is recorded in
// the returned `gaps` list (pinned by the adapter golden tests).

import { HANDOFF_SCHEMA, REVIEW_EXPORT_SCHEMA } from "./constants"
import { applyExamLevelReportRollup, normalizeConfirmedBpm } from "./exam-rollup"

const deepCopy = (value) => JSON.parse(JSON.stringify(value))

// No accepted recording, no report — the same rule (and near-identical
// wording) as the handoff viewer, which refuses the export with "Accept at
// least one recording before creating the Report draft." The review page
// checks this before posting a snapshot; the adapter repeats it for any
// snapshot that reaches it regardless.
export const NO_ACCEPTED_RECORDINGS_MESSAGE = "Accept at least one recording before opening the Medaica Report."
export const ACCEPTED_WITHOUT_EVIDENCE_MESSAGE =
  "The accepted recordings have no analysis results; accept a recording whose analysis completed before opening the Medaica Report."
const finiteBpm = (entry) => Number.isFinite(Number(entry.evidencePackage.evidence?.heartRate?.valueBpm))

// Which evidence domains of an accepted recording feed the report. A
// disposition saved before domains existed carries none and counts for both;
// a recording that is not accepted contributes neither.
const BOTH_DOMAINS = { timing: true, structuralSound: true }
const NO_DOMAINS = { timing: false, structuralSound: false }
export const acceptedDomainsOf = (entry) => {
  const disposition = (entry && entry.disposition) || {}
  if (disposition.decision !== "accepted") return { ...NO_DOMAINS }
  const domains = disposition.acceptedDomains
  if (!domains) return { ...BOTH_DOMAINS }
  return { timing: Boolean(domains.timing), structuralSound: Boolean(domains.structuralSound) }
}

// The position a recording is named by throughout the report: the server-
// derived site label ("Aortic"), else the raw auscultation point for exports
// that predate the site field, else null.
const positionOf = (entry) => (entry.site && entry.site.label) || entry.auscultationPoint || null

// The heart rate the reviewer confirmed on the recording in the viewer's
// note dialog (a whole number 25–220), or null. The roll-up decides whether
// it counts: only on a recording accepted for Timing.
const confirmedBpmOf = (entry) =>
  normalizeConfirmedBpm(
    entry.disposition && entry.disposition.observations && entry.disposition.observations.confirmedBpm
  )

// Whether the reviewer accepted a vendor result on the recording in the
// viewer's plugin rack
const hasAcceptedPluginResult = (entry) => {
  const reviews = entry.disposition && entry.disposition.pluginReviews
  return !!reviews && Object.values(reviews).some((review) => review && review.status === "accepted")
}

// The exam-level roll-up (heart-rate cluster, Cycle Regularity, Structural
// Sound source, Signal Quality, scope, audit block) runs here over the
// evidence domains accepted on each recording — see exam-rollup.js.
// `now` exists for deterministic tests (the audit block's createdAt).
export function adaptReviewExport(
  exportPayload,
  { primaryRecordingId = null, now = () => new Date().toISOString() } = {}
) {
  const gaps = []
  const gap = (code, detail) => gaps.push({ code, detail })

  if (!exportPayload || exportPayload.schema !== REVIEW_EXPORT_SCHEMA) {
    return {
      ok: false,
      pkg: null,
      gaps: [{ code: "unknown_schema", detail: `expected ${REVIEW_EXPORT_SCHEMA}` }],
      meta: null,
    }
  }
  gap(
    "schema_wrapper_mismatch",
    `Report requires pkg.schema === '${HANDOFF_SCHEMA}' and would reject the export wrapper ('${exportPayload.schema}') outright; the handoff packages live one level down at recordings[i].evidencePackage.`
  )

  const entries = Array.isArray(exportPayload.recordings) ? exportPayload.recordings : []
  const withEvidence = entries.filter((r) => r && r.evidencePackage && r.evidencePackage.schema === HANDOFF_SCHEMA)
  const withoutEvidence = entries.filter((r) => !r || !r.evidencePackage)
  if (withoutEvidence.length) {
    gap(
      "recording_without_evidence",
      `${withoutEvidence.length} recording(s) carry no evidencePackage (e.g. guideStatus 'error') and cannot appear in the report at all — the report has no 'guide failed' card state.`
    )
  }

  const decisionOf = (r) => (r.disposition && r.disposition.decision) || "none"
  const acceptedAny = entries.filter((r) => r && decisionOf(r) === "accepted")
  const accepted = withEvidence.filter((r) => decisionOf(r) === "accepted")
  const rejected = withEvidence.filter((r) => decisionOf(r) === "rejected")
  const undecided = withEvidence.filter((r) => !["accepted", "rejected"].includes(decisionOf(r)))

  if (rejected.length || undecided.length) {
    gap(
      "dispositions_not_modeled",
      `Report has no concept of viewer reviewer dispositions. Adapter policy: only 'accepted' recordings feed the report (${accepted.length} accepted, ${rejected.length} rejected excluded, ${undecided.length} undecided excluded). Rejected/undecided evidence is dropped, not shown as excluded-with-reason.`
    )
  }
  if (rejected.some((r) => (r.disposition.rejectReasons || []).length || (r.disposition.note || "").trim())) {
    gap(
      "reject_reasons_dropped",
      "Viewer reject reasons/notes on rejected recordings have no destination in the report model."
    )
  }

  if (!accepted.length) {
    if (acceptedAny.length) gap("accepted_without_evidence", ACCEPTED_WITHOUT_EVIDENCE_MESSAGE)
    else gap("no_accepted_recordings", NO_ACCEPTED_RECORDINGS_MESSAGE)
    return {
      ok: false,
      pkg: null,
      gaps,
      meta: { accepted: accepted.length, rejected: rejected.length, undecided: undecided.length },
    }
  }
  const pool = accepted

  // The base package supplies what the roll-up does not own (recording and
  // screening context, questionnaire, reviewer notes). Every report card is
  // rolled, so the cards are invariant to this choice; prefer a recording
  // with a heart rate for the legacy single-recording wording.
  const primaryEntry =
    (primaryRecordingId && pool.find((r) => r.recordingId === primaryRecordingId)) || pool.find(finiteBpm) || pool[0]
  const pkg = deepCopy(primaryEntry.evidencePackage)

  // Recording chips, in the review's recording order: one per pooled
  // recording, plus any recording the reviewer accepted a vendor result on in
  // the plugin rack — that result shows in the cards' Evidence panels although
  // the recording itself contributes nothing. Positions from the wrapper
  // entry (the per-recording packages carry position: null — gap).
  const poolIds = new Set(pool.map((entry) => entry.recordingId))
  const chipRecordings = entries
    .filter((entry) => entry && entry.recordingId && (poolIds.has(entry.recordingId) || hasAcceptedPluginResult(entry)))
    .map((entry) => {
      const epRec =
        (entry.evidencePackage && entry.evidencePackage.recordings && entry.evidencePackage.recordings[0]) || {}
      return {
        ...deepCopy(epRec),
        recordingId: entry.recordingId,
        position: epRec.position || positionOf(entry),
        acceptedForExamRollup: poolIds.has(entry.recordingId),
        acceptedDomains: acceptedDomainsOf(entry),
        // Stored ACT / BeatAware results and the reviewer's plugin reviews;
        // absent on snapshots written before the export carried them
        vendorEvidence: entry.vendorEvidence || null,
        pluginReviews: (entry.disposition && entry.disposition.pluginReviews) || null,
      }
    })
  if (
    pool.some((entry) => !((entry.evidencePackage.recordings || [])[0] || {}).position) &&
    pool.some((entry) => entry.auscultationPoint)
  ) {
    gap(
      "position_only_in_wrapper",
      "Per-recording evidence packages carry position: null; the site exists only on the wrapper entries. Adapter copies it in; evidence sourcePosition stays null inside findings text."
    )
  }
  pkg.recordings = chipRecordings
  pkg.examId = pkg.examId || exportPayload.examId || null

  // Exam-level roll-up over the contributing (accepted) recordings.
  const rollupRecordings = entries
    .filter((r) => r && r.recordingId)
    .map((entry, index) => {
      const handoff = entry.evidencePackage?.schema === HANDOFF_SCHEMA ? entry.evidencePackage : null
      return {
        recordingId: entry.recordingId,
        position: positionOf(entry),
        recordingSequence: index + 1,
        reviewStatus: decisionOf(entry),
        evidence: handoff ? handoff.evidence : null,
        // A timing-engine guide's Evaluation Range provenance: its timing is
        // the range's alone (absent on other packages and older snapshots)
        evaluationRange: handoff?.evaluationRange || null,
        contributes: poolIds.has(entry.recordingId),
        acceptedDomains: acceptedDomainsOf(entry),
        // The pipeline's whole-recording timing metrics (uncorrected); absent
        // on snapshots written before the export carried them.
        pipelineMetrics: entry.pipelineMetrics || null,
        hcpConfirmedBpm: confirmedBpmOf(entry),
      }
    })
  applyExamLevelReportRollup(pkg, rollupRecordings, { now })

  // Exam-level timing consistency (pcg-core's per-recording half/double
  // correction context): no consumer in the report itself — the roll-up
  // works from the already-corrected per-recording rates. Passed through.
  const etc = exportPayload.examTimingConsistency || null
  if (etc) {
    pkg.examTimingConsistency = deepCopy(etc)
    gap(
      "exam_timing_consistency_unconsumed",
      "examTimingConsistency (median BPM, per-recording consistency, corrected BPMs) has no consumer in the report; passed through as pkg.examTimingConsistency."
    )
  }

  if (entries.some((r) => r.phaseConfirmation === null || r.phaseConfirmation === undefined)) {
    gap(
      "phase_confirmation_unconsumed",
      "Wrapper-level phaseConfirmation is not read by the report; structural phase state arrives only via evidence.structuralSoundDetail.phaseStatus."
    )
  }

  const context = pkg.context || {}
  if (!context.currentQuestionnaire) {
    gap(
      "questionnaire_absent",
      "Export carries currentQuestionnaire: null. The prototype would silently keep its embedded DEMO questionnaire in that case — the vendored default report ships questionnaire: null instead (deliberate deviation)."
    )
  }

  return {
    ok: true,
    pkg,
    gaps,
    meta: {
      examId: exportPayload.examId,
      exportVersion: exportPayload.version,
      primaryRecordingId: primaryEntry.recordingId,
      primaryPosition: primaryEntry.auscultationPoint || null,
      accepted: accepted.length,
      rejected: rejected.length,
      undecided: undecided.length,
      withoutEvidence: withoutEvidence.length,
      examMedianBpm: etc ? etc.medianBpm : null,
      rollup: {
        contributingRecordingIds: pkg.examRollup.contributingRecordingIds,
        timingIncludedRecordingIds: pkg.examRollup.timing.includedRecordingIds,
        timingExcludedRecordingIds: pkg.examRollup.timing.excludedRecordingIds,
        timingDiscrepantRecordingIds: pkg.examRollup.timing.discrepantRecordingIds,
        reviewRequired: pkg.examRollup.timing.reviewRequired,
        structuralSoundSourceRecordingId: pkg.examRollup.structuralSound.sourceRecordingId,
      },
    },
  }
}
